'use client';

import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, X, Filter } from 'lucide-react';

export interface PolicyFiltersState {
  search: string;
  effectType: 'all' | 'permit' | 'forbid';
  resourceType: string | null;
  showDefaults: boolean;
}

export interface PolicyFiltersProps {
  filters: PolicyFiltersState;
  onFiltersChange: (filters: PolicyFiltersState) => void;
  resourceTypes?: string[];
}

export function PolicyFilters({
  filters,
  onFiltersChange,
  resourceTypes = ['Matricks::Workflow', 'Matricks::Contact', 'Matricks::Provider', 'Matricks::AgentPipeline']
}: PolicyFiltersProps) {
  const hasActiveFilters =
    filters.search ||
    filters.effectType !== 'all' ||
    filters.resourceType !== null ||
    !filters.showDefaults;

  const clearFilters = () => {
    onFiltersChange({
      search: '',
      effectType: 'all',
      resourceType: null,
      showDefaults: true
    });
  };

  return (
    <div className="space-y-3">
      {/* Search Bar */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search policies..."
            value={filters.search}
            onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
            className="pl-9 h-9"
          />
        </div>
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 h-9">
            <X className="h-4 w-4 mr-1" /> Clear filters
          </Button>
        )}
      </div>

      {/* Filter Chips */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-4 w-4 text-slate-400" />

        {/* Effect Type Filter */}
        <div className="flex items-center gap-1 border rounded-lg p-0.5 bg-slate-50">
          <FilterChip
            label="All"
            active={filters.effectType === 'all'}
            onClick={() => onFiltersChange({ ...filters, effectType: 'all' })}
          />
          <FilterChip
            label="Allow"
            active={filters.effectType === 'permit'}
            onClick={() => onFiltersChange({ ...filters, effectType: 'permit' })}
            activeClassName="bg-green-100 text-green-700 border-green-200"
          />
          <FilterChip
            label="Deny"
            active={filters.effectType === 'forbid'}
            onClick={() => onFiltersChange({ ...filters, effectType: 'forbid' })}
            activeClassName="bg-red-100 text-red-700 border-red-200"
          />
        </div>

        {/* Resource Type Filter */}
        <div className="flex items-center gap-1">
          {resourceTypes.map(rt => {
            const shortName = rt.split('::')[1] || rt;
            const isActive = filters.resourceType === rt;
            return (
              <Badge
                key={rt}
                variant={isActive ? 'default' : 'outline'}
                className={`cursor-pointer transition-all ${
                  isActive
                    ? 'bg-indigo-600 hover:bg-indigo-700'
                    : 'hover:bg-slate-100'
                }`}
                onClick={() =>
                  onFiltersChange({
                    ...filters,
                    resourceType: isActive ? null : rt
                  })
                }
              >
                {shortName}
              </Badge>
            );
          })}
        </div>

        {/* Show Defaults Toggle */}
        <div className="ml-auto">
          <Badge
            variant={filters.showDefaults ? 'outline' : 'secondary'}
            className="cursor-pointer"
            onClick={() => onFiltersChange({ ...filters, showDefaults: !filters.showDefaults })}
          >
            {filters.showDefaults ? 'Showing defaults' : 'Hiding defaults'}
          </Badge>
        </div>
      </div>
    </div>
  );
}

interface FilterChipProps {
  label: string;
  active: boolean;
  onClick: () => void;
  activeClassName?: string;
}

function FilterChip({ label, active, onClick, activeClassName = 'bg-white shadow-sm' }: FilterChipProps) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
        active
          ? activeClassName
          : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      {label}
    </button>
  );
}

// Helper to apply filters to policy list
export function applyPolicyFilters<T extends {
  name: string;
  description?: string;
  policyText: string;
  isDefault?: boolean;
}>(policies: T[], filters: PolicyFiltersState): T[] {
  return policies.filter(policy => {
    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      const matchesSearch =
        policy.name.toLowerCase().includes(searchLower) ||
        (policy.description?.toLowerCase().includes(searchLower) ?? false) ||
        policy.policyText.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Effect type filter
    if (filters.effectType !== 'all') {
      const isPermit = policy.policyText.trim().startsWith('permit');
      if (filters.effectType === 'permit' && !isPermit) return false;
      if (filters.effectType === 'forbid' && isPermit) return false;
    }

    // Resource type filter
    if (filters.resourceType) {
      if (!policy.policyText.includes(filters.resourceType)) return false;
    }

    // Show defaults filter
    if (!filters.showDefaults && policy.isDefault) return false;

    return true;
  });
}
